import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core/dist/types";
import {
  restrictToHorizontalAxis,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { Fragment, useEffect, useState } from "react";
import { useMutation } from "urql";
import Auth from "../../auth/Auth";
import { graphql } from "../../gql";
import { DerivedImageUb } from "../../gql/graphql";
import { DraggableImage } from "./DraggableImage";

interface Props {
  auth: Auth;
  bucket: string;
  songId: string;
  derivedImages: DerivedImageUb[];
  reexecuteQuery: () => void;
  isDisabled?: boolean;
}

const updateOrderOfDerivedImagesMutation = graphql(`
  mutation updateOrderOfDerivedImages(
    $bucket: String!
    $id: ID!
    $keysNewOrder: [String!]!
  ) {
    updateOrderOfDerivedImages(
      bucket: $bucket
      id: $id
      keysNewOrder: $keysNewOrder
    ) {
      ...SongFields
    }
  }
`);

export function DisplayImages({
  auth,
  bucket,
  songId,
  derivedImages,
  reexecuteQuery,
  isDisabled = false,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const [localDerivedImages, setLocalDerivedImages] = useState(derivedImages);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
  const [, executeUpdateOrderMutation] = useMutation(
    updateOrderOfDerivedImagesMutation,
  );

  // Sync local state with prop changes (e.g., after deletion)
  useEffect(() => {
    setLocalDerivedImages(derivedImages);
  }, [derivedImages]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over }: DragEndEvent = event;

    if (active && over && active.id !== over.id) {
      setLocalDerivedImages((previousOrder) => {
        const oldIndex = previousOrder.findIndex((e) => e.key === active.id);
        const newIndex = previousOrder.findIndex((e) => e.key === over.id);

        const newOrder = arrayMove(previousOrder, oldIndex, newIndex);
        const keysNewOrder = newOrder.map((img) => img.key);

        setIsUpdatingOrder(true);
        executeUpdateOrderMutation({
          bucket,
          id: songId,
          keysNewOrder,
        })
          .then((result) => {
            console.debug("Updated order of derived images", result);
            setIsUpdatingOrder(false);

            if (result.error) {
              console.error(
                "Failed to update order, rolling back:",
                result.error,
              );
              setLocalDerivedImages(previousOrder);
            }
          })
          .catch((error) => {
            console.error(
              "Exception during order update, rolling back:",
              error,
            );
            setIsUpdatingOrder(false);
            setLocalDerivedImages(previousOrder);
          });

        //Optimistic update --> show new order immediately.
        return newOrder;
      });
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      modifiers={[restrictToHorizontalAxis, restrictToWindowEdges]}
    >
      <SortableContext
        items={localDerivedImages.map((img) => img.key)}
        strategy={horizontalListSortingStrategy}
      >
        <div className="d-flex overflow-x-auto">
          {localDerivedImages.map((entry) => (
            <Fragment key={entry.key}>
              <DraggableImage
                auth={auth}
                bucket={bucket}
                songId={songId}
                derivedImage={entry}
                isUpdatingOrder={isUpdatingOrder}
                reexecuteQuery={reexecuteQuery}
                isDisabled={isDisabled}
              />
            </Fragment>
          ))}
        </div>
      </SortableContext>

      {/* <DragOverlay>
                <div style={{ backgroundColor: 'red', width: '2px', height: '100%' }} />
            </DragOverlay> */}
    </DndContext>
  );
}
